import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { UsersService } from '../users.service';
import { UserFormData, FieldValidationState, Supervisor, SupervisoryLevel, GeoNode } from '../users.model';
import { GeographicNodesService } from '../../geographic-nodes/geographic-nodes.service';
import { GeographicNode } from '../../geographic-nodes/geographic-node.model';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';

@Component({
  selector: 'app-create-user',
  standalone: false,
  templateUrl: './create-user.component.html',
  styleUrl: './create-user.component.scss'
})
export class CreateUserComponent implements OnInit, OnDestroy {
  private usersService = inject(UsersService);
  private geoNodesService = inject(GeographicNodesService);
  private router = inject(Router);
  private _subs: Subscription[] = [];
  private geoSearchSubject = new Subject<string>();

  // Per-button emitters — subscribed below, passed to the page header
  readonly cancelAction = new EventEmitter<void>();
  readonly createUserAction = new EventEmitter<void>();

  // Form Data
  formData: UserFormData = {
    fullName: '',
    email: '',
    phone: '',
    role: null,
    PlatformAccess: 'BOTH',
    supervisoryLevel: null,
    supervisorId: null,
    supervisor: null,
    geographicNodeId: null,
    geographicNodeLabel: null
  };

  // Validation States for child components
  emailState: FieldValidationState = { status: 'idle' };
  phoneState: FieldValidationState = { status: 'idle' };

  // Error flags for simple inputs
  fullNameError = false;
  supervisoryLevelError = false;
  roleError = false;
  supervisorError = false;

  // Global Page States
  submitted = false;
  saving = false;

  // Conflict / Save Error State
  saveError: { type: string; message: string; code?: string } | null = null;

  // Success state
  creationSuccess = false;
  createdUserName = '';
  createdUserEmail = '';
  remainingSeconds = 10;
  private redirectTimer: any = null;

  geoNodes: GeoNode[] = [];
  geoLoading = false;

  // Role Permissions Preview hardcoded map
  ROLE_PERMISSIONS: Record<SupervisoryLevel, string[]> = {
    L3: ['Manage own region agents', 'Dispatch & operations', 'Region reports'],
    L2: ['Manage own region agents', 'Dispatch & operations', 'Region reports'],
    L1: ['Execute assigned tasks']
  };

  // Modal visibility and level-required guard
  showSupervisorModal = false;
  showSupervisoryLevelRequiredError = false;

  // Header actions exposed to the template
  get headerActions(): HeaderAction[] {
    return [
      { label: 'Cancel', variant: 'secondary', disabled: this.saving || this.creationSuccess, emitter: this.cancelAction },
      { label: 'Create User', variant: 'primary', disabled: !this.isFormReadyToSubmit || this.saving || this.creationSuccess, emitter: this.createUserAction },
    ];
  }

  /**
   * Non-mutating validity check: drives the disabled state of the "Create User"
   * button without setting error flags prematurely.
   */
  get isFormReadyToSubmit(): boolean {
    const nameOk = !!this.formData.fullName.trim();
    const emailOk = this.emailState.status === 'valid';
    const phoneOk = this.phoneState.status === 'valid';
    const levelOk = !!this.formData.supervisoryLevel;
    const roleOk = !!this.formData.role;
    const supervisorOk = this.formData.supervisoryLevel === 'L3' || !!this.formData.supervisor;
    return nameOk && emailOk && phoneOk && levelOk && roleOk && supervisorOk;
  }

  ngOnInit(): void {
    this._subs.push(
      this.cancelAction.subscribe(() => this.onCancel()),
      this.createUserAction.subscribe(() => this.onCreateUser()),
    );

    this.loadGeoNodes();

    this._subs.push(
      this.geoSearchSubject
        .pipe(
          debounceTime(400),
          switchMap((query) => {
            this.geoLoading = true;
            return this.geoNodesService.findAll({ search: query || undefined, limit: 20 });
          })
        )
        .subscribe({
          next: (res) => {
            this.geoNodes = this.toGeoNodes(res.data);
            this.geoLoading = false;
          },
          error: () => { this.geoLoading = false; },
        })
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this.clearRedirectTimer();
  }

  // Get current status of each of the required fields
  getFieldStatus(fieldName: string): 'empty' | 'valid' | 'invalid' {
    switch (fieldName) {
      case 'fullName':
        if (!this.formData.fullName.trim()) {
          return this.submitted && this.fullNameError ? 'invalid' : 'empty';
        }
        return 'valid';
      case 'email':
        if (!this.formData.email.trim()) {
          return this.submitted ? 'invalid' : 'empty';
        }
        if (this.emailState.status === 'error' || this.emailState.status === 'conflict') {
          return 'invalid';
        }
        return this.emailState.status === 'valid' ? 'valid' : 'empty';
      case 'phone':
        if (!this.formData.phone.trim()) {
          return this.submitted ? 'invalid' : 'empty';
        }
        if (this.phoneState.status === 'error' || this.phoneState.status === 'conflict') {
          return 'invalid';
        }
        return this.phoneState.status === 'valid' ? 'valid' : 'empty';
      case 'supervisoryLevel':
        if (!this.formData.supervisoryLevel) {
          return this.submitted && this.supervisoryLevelError ? 'invalid' : 'empty';
        }
        return 'valid';
      case 'role':
        if (!this.formData.role) {
          return this.submitted && this.roleError ? 'invalid' : 'empty';
        }
        return 'valid';
      case 'supervisor':
        if (this.formData.supervisoryLevel === 'L3') {
          return 'valid';
        }
        if (!this.formData.supervisor) {
          return this.submitted && this.supervisorError ? 'invalid' : 'empty';
        }
        return 'valid';
      default:
        return 'empty';
    }
  }

  // Count how many required fields are valid
  get filledFieldsCount(): number {
    let count = 0;
    if (this.getFieldStatus('fullName') === 'valid') count++;
    if (this.getFieldStatus('email') === 'valid') count++;
    if (this.getFieldStatus('phone') === 'valid') count++;
    if (this.getFieldStatus('supervisoryLevel') === 'valid') count++;
    if (this.getFieldStatus('role') === 'valid') count++;
    if (this.formData.supervisoryLevel !== 'L3' && this.getFieldStatus('supervisor') === 'valid') count++;
    return count;
  }

  get totalRequiredFields(): number {
    return this.formData.supervisoryLevel === 'L3' ? 5 : 6;
  }

  // Main Form Validation Check
  isFormValid(): boolean {
    // Reset individual error states
    this.fullNameError = !this.formData.fullName.trim();
    this.supervisoryLevelError = !this.formData.supervisoryLevel;
    this.roleError = !this.formData.role;
    this.supervisorError = this.formData.supervisoryLevel !== 'L3' && !this.formData.supervisor;

    // Check email uniqueness/format
    const emailValid = this.emailState.status === 'valid';
    // Check phone uniqueness/format
    const phoneValid = this.phoneState.status === 'valid';

    return !this.fullNameError &&
      !this.supervisoryLevelError &&
      !this.roleError &&
      !this.supervisorError &&
      emailValid &&
      phoneValid;
  }

  // Get active issues list for top of validation summary
  get validationIssues(): string[] {
    const issues: string[] = [];
    if (this.fullNameError) {
      issues.push('Full name is required');
    }
    if (!this.formData.email.trim()) {
      issues.push('Email is required');
    } else if (this.emailState.status === 'error') {
      issues.push(this.emailState.message || 'Email format is invalid');
    } else if (this.emailState.status === 'conflict') {
      issues.push('Email is already in use');
    } else if (this.emailState.status !== 'valid') {
      issues.push('Email validation is pending');
    }

    if (!this.formData.phone.trim()) {
      issues.push('Phone is required');
    } else if (this.phoneState.status === 'error') {
      issues.push(this.phoneState.message || 'Phone format is invalid');
    } else if (this.phoneState.status === 'conflict') {
      issues.push('Phone number is already in use');
    } else if (this.phoneState.status !== 'valid') {
      issues.push('Phone validation is pending');
    }

    if (this.supervisoryLevelError) {
      issues.push('Supervisory Level is required');
    }
    if (this.roleError) {
      issues.push('Role is required');
    }
    if (this.formData.supervisoryLevel !== 'L3' && this.supervisorError) {
      issues.push('Supervisor is required');
    }
    return issues;
  }

  // Open modal — guard: level must be chosen first
  onPickSupervisor(): void {
    if (!this.formData.supervisoryLevel) {
      this.showSupervisoryLevelRequiredError = true;
      return;
    }
    this.showSupervisoryLevelRequiredError = false;
    this.showSupervisorModal = true;
  }

  closeSupervisorModal(): void {
    this.showSupervisorModal = false;
  }

  // Called when the modal emits a selected supervisor
  onSupervisorSelected(supervisor: Supervisor): void {
    this.formData.supervisor = supervisor;
    this.formData.supervisorId = supervisor.id;
    this.supervisorError = false;
    this.showSupervisorModal = false;
  }

  // Clear supervisor selection
  clearSupervisor(): void {
    this.formData.supervisor = null;
    this.formData.supervisorId = null;
  }

  // Handle Level selection
  onLevelChange(level: SupervisoryLevel): void {
    this.formData.supervisoryLevel = level;
    this.supervisoryLevelError = false;
    this.showSupervisoryLevelRequiredError = false;
    this.clearSupervisor();
  }

  // Handle Role selection
  onRoleChange(role: { id: string; label: string } | null): void {
    if (role) {
      this.formData.role = { id: role.id, name: role.label };
    } else {
      this.formData.role = null;
    }
    this.roleError = false;
  }

  onGeoSelectionChange(ids: string[]): void {
    this.formData.geographicNodeIds = ids;
  }

  onGeoSearch(query: string): void {
    this.geoSearchSubject.next(query);
  }

  private loadGeoNodes(): void {
    this.geoLoading = true;
    this.geoNodesService.findAll({ limit: 500, status: 'active' }).subscribe({
      next: (res) => {
        this.geoNodes = this.buildTree(res.data);
        this.geoLoading = false;
      },
      error: () => { this.geoLoading = false; },
    });
  }

  private buildTree(nodes: GeographicNode[]): GeoNode[] {
    const map = new Map<string, GeoNode>();
    for (const n of nodes) {
      map.set(n.id, { id: n.id, label: n.name, code: n.code, userCount: n.activePosCount ?? 0, children: [], expanded: true });
    }
    const roots: GeoNode[] = [];
    for (const n of nodes) {
      const node = map.get(n.id)!;
      if (n.parent_id && map.has(n.parent_id)) {
        map.get(n.parent_id)!.children!.push(node);
      } else {
        roots.push(node);
      }
    }
    for (const node of map.values()) {
      if (!node.children?.length) delete node.children;
    }
    return roots;
  }

  private toGeoNodes(nodes: GeographicNode[]): GeoNode[] {
    return nodes.map(n => ({ id: n.id, label: n.name, code: n.code, userCount: n.activePosCount ?? 0 }));
  }

  // Submit flow
  onCreateUser() {
    this.submitted = true;
    this.saveError = null;

    if (!this.isFormValid()) {
      return;
    }

    this.saving = true;

    this.usersService.createUser(this.formData).subscribe({
      next: () => {
        this.saving = false;
        this.createdUserName = this.formData.fullName;
        this.createdUserEmail = this.formData.email;
        this.creationSuccess = true;
        this.startRedirectTimer();
      },
      error: (err) => {
        this.saving = false;
        this.handleSaveError(err);
      }
    });
  }

  startRedirectTimer() {
    this.clearRedirectTimer();
    this.remainingSeconds = 10;
    this.redirectTimer = setInterval(() => {
      this.remainingSeconds--;
      if (this.remainingSeconds <= 0) {
        this.redirectToUserList();
      }
    }, 1000);
  }

  clearRedirectTimer() {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
      this.redirectTimer = null;
    }
  }

  redirectToUserList(): void {
    this.clearRedirectTimer();
    this.router.navigate(['/users']);
  }

  onResetForm() {
    this.clearRedirectTimer();
    this.creationSuccess = false;
    this.submitted = false;
    this.saving = false;
    this.saveError = null;
    this.formData = {
      fullName: '',
      email: '',
      phone: '',
      role: null,
      PlatformAccess: 'BOTH',
      supervisoryLevel: null,
      supervisorId: null,
      supervisor: null,
      geographicNodeId: null,
      geographicNodeLabel: null
    };
    this.emailState = { status: 'idle' };
    this.phoneState = { status: 'idle' };
    this.fullNameError = false;
    this.supervisoryLevelError = false;
    this.roleError = false;
    this.supervisorError = false;
  }

  // Handle Save Errors (409 Conflict etc.)
  handleSaveError(err: any) {
    if (err.status === 409) {
      const code = err.error?.code;
      if (code === 'CONFLICT_PHONE_DUPLICATE') {
        this.saveError = {
          type: 'phone_conflict',
          code: 'CONFLICT_PHONE_DUPLICATE',
          message: `Phone ${this.formData.phone} was just registered to another user (created ${err.error.secondsAgo || 45} seconds ago). Your form data has been preserved.`
        };
        // Update phone state to conflict
        this.phoneState = {
          status: 'conflict',
          message: this.saveError.message
        };
      } else if (code === 'CONFLICT_EMAIL_DUPLICATE') {
        this.saveError = {
          type: 'email_conflict',
          code: 'CONFLICT_EMAIL_DUPLICATE',
          message: `Email ${this.formData.email} is already in use by another user.`
        };
        this.emailState = {
          status: 'conflict',
          conflictUser: { full_name: 'Sara Ridouani', id: 'USR-04829' },
          message: this.saveError.message
        };
      } else {
        this.saveError = {
          type: 'generic',
          message: 'A conflict was detected at save time.'
        };
      }
    } else {
      this.saveError = {
        type: 'generic',
        message: err.message || 'An unexpected error occurred while saving the user.'
      };
    }
  }

  // Conflict Action Handlers
  onDiscardConflict(): void {
    // Navigate back or reset form
    this.router.navigate(['/users']);
  }

  onEditPhoneConflict(): void {
    // Dismiss conflict panel to let user edit the form
    this.saveError = null;
    // Set focus or reset validation
    this.phoneState = { status: 'idle' };
  }

  onEditEmailConflict(): void {
    this.saveError = null;
    this.emailState = { status: 'idle' };
  }

  onCancel(): void {
    this.router.navigate(['/users']);
  }
}
